// The conversation screen — a port of `ui/ChatScreen.kt`.
import { useEffect, useRef } from 'react';

import MessageBubble from '../components/MessageBubble';
import SpeechNotice from '../components/SpeechNotice';
import { useStore } from '../store';
import type { Scenario } from '../tutor/scenarios';

export default function ChatScreen({ scenario }: { scenario: Scenario }) {
  const s = useStore();
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the newest bubble in view, including while the tutor is "thinking".
  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [s.messages, s.tutorTyping]);

  const canSend = s.input.trim() !== '' && !s.tutorTyping;

  return (
    <div className="screen">
      <div className="topbar">
        <button type="button" onClick={s.exitChat} aria-label="Back to the scenario list">
          ←
        </button>
        <div className="title">
          <strong>
            {scenario.emoji} {scenario.title}
          </strong>
          <span>Speaking with your {scenario.role}</span>
        </div>
        <button
          type="button"
          onClick={s.toggleVoiceReplies}
          aria-label={s.voiceReplies ? 'Mute the tutor' : 'Let the tutor speak'}
          title={s.voiceName ? `Tutor's voice: ${s.voiceName}` : undefined}
        >
          {s.voiceReplies ? '🔊' : '🔇'}
        </button>
      </div>

      <SpeechNotice />

      <div className="messages" ref={listRef}>
        {s.messages.map((message) => (
          <MessageBubble key={message.id} message={message} onSpeak={() => s.speakAgain(message.text)} />
        ))}
        {s.tutorTyping && (
          <div className="typing">
            <span className="spinner" role="status" aria-label="The tutor is thinking" />
            AI tutor is thinking…
          </div>
        )}
      </div>

      <div className="inputbar">
        <div className="options">
          <button type="button" onClick={s.askForHint}>
            Need a hint?
          </button>
          <button type="button" onClick={s.toggleCorrections}>
            {s.correctionsEnabled ? '✏️ Corrections on' : '💬 Corrections off'}
          </button>
          {s.isListening && <span className="mic-state">🎙 Mic on</span>}
        </div>

        <div className="compose">
          <textarea
            value={s.input}
            onChange={(e) => s.setInput(e.target.value)}
            rows={1}
            placeholder={
              s.isListening
                ? "Listening — talk away, tap ⏹ when you're done"
                : 'Say something in English…'
            }
            aria-label="Your message"
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter starts a new line — the web equivalent
              // of the Android IME's Send action.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                s.send();
              }
            }}
          />
          {s.micSupported && (
            <button
              type="button"
              className={`round-button ${s.isListening ? 'listening' : ''}`}
              onClick={s.toggleMic}
              aria-label={s.isListening ? 'Stop listening' : 'Answer out loud'}
            >
              {s.isListening ? '⏹' : '🎤'}
            </button>
          )}
          <button
            type="button"
            className="round-button send"
            onClick={s.send}
            disabled={!canSend}
            aria-label="Send"
          >
            ➤
          </button>
        </div>
      </div>
    </div>
  );
}
